import { forwardRef, HTMLProps, ReactElement, ReactNode } from 'react';
import styled from 'styled-components';
import { alpha } from '@mui/system/colorManipulator';

import { IconPointFilled } from '@u21/tabler-icons';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';
import { formatDatetime } from 'app/shared/utils/date';
import { ColorSchema } from 'vendor/material-minimal/palette';
import { getDOMProps } from 'app/shared/utils/react';

export type U21TimelineEntryColor = 'default' | ColorSchema;

export interface U21TimelineProps extends HTMLProps<HTMLDivElement> {
  children: NonNullable<ReactNode>;
}

export interface U21TimelineEntryProps extends HTMLProps<HTMLDivElement> {
  timestamp?: string;
  color?: U21TimelineEntryColor;
  icon?: ReactElement;
  children: ReactNode;
}

export const U21Timeline = ({ children, ...rest }: U21TimelineProps) => {
  return (
    <U21Spacer spacing={3} useFlexGap {...getDOMProps(rest)}>
      {children}
    </U21Spacer>
  );
};

export const U21TimelineEntry = forwardRef<
  HTMLDivElement,
  U21TimelineEntryProps
>((props, ref) => {
  const { timestamp, color = 'default', icon, children, ...rest } = props;
  return (
    <Row ref={ref} {...getDOMProps(rest)}>
      <NodeColumn>
        <Node $color={color}>{icon ?? <IconPointFilled />}</Node>
      </NodeColumn>
      <Content spacing={0.5} useFlexGap>
        {children}
        {timestamp && (
          <U21Typography variant="caption" color="text.secondary">
            {formatDatetime(timestamp)}
          </U21Typography>
        )}
      </Content>
    </Row>
  );
});

const Row = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const Content = styled(U21Spacer)`
  flex: 1;
  min-width: 0;
`;

const NodeColumn = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  flex: 0 0 24px;

  // Connecting line behind the nodes, hidden on the last entry.
  &::before {
    content: '';
    position: absolute;
    top: 24px;
    bottom: ${({ theme }) => `-${theme.spacing(3)}`};
    width: 2px;
    background: ${({ theme }) => theme.palette.divider};
  }

  ${Row}:last-child &::before {
    display: none;
  }
`;

const Node = styled.div<{ $color: U21TimelineEntryColor }>`
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ theme, $color }) =>
    alpha(
      $color === 'default'
        ? theme.palette.grey[500]
        : theme.palette[$color].main,
      0.08,
    )};
  color: ${({ theme, $color }) =>
    $color === 'default'
      ? theme.palette.grey[500]
      : theme.palette[$color].main};
`;
